import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRight } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";

import { dateFilterOptions } from "@/B2C/features/dashboard/services/dashboardService";
import { Button, SelectInput } from "@/shared/components";
import { B2C_FEATURE_FLAGS } from "@/shared/config/header.config";

import { usePosterReducers } from "@/shared/redux/store/useGetData";
import { getPeriodGranularity } from "../../constants/invoicePeriod";
import {
  getDefaultPeriodForFilter,
  getInsurerCountryById,
  getInvoicePeriodOptions,
  getInvoiceTaxSplit,
  getPendingInvoiceLineCount,
} from "../../services/mockData";
import type { GenerateInvoiceFormValues } from "../../types";
import { generateInvoiceSchema } from "../../validations/invoiceSchemas";
import InvoicePreviewStep from "./InvoicePreviewStep";

type GenerateInvoicesModalProps = {
  isSubmitting?: boolean;
  onSubmit: (values: GenerateInvoiceFormValues, lineCount: number) => Promise<void> | void;
  onCancel: () => void;
};

export default function GenerateInvoicesModal({
  isSubmitting = false,
  onSubmit,
  onCancel,
}: GenerateInvoicesModalProps) {
  const {
    commissionLedger: { insurers },
  } = usePosterReducers();

  const insurerOptions = useMemo(
    () =>
      (insurers || [])
        .map((item) => ({ value: String(item?.id ?? ""), label: item?.name || "-" }))
        .filter((option) => option.value !== ""),
    [insurers]
  );

  const methods = useForm<GenerateInvoiceFormValues>({
    mode: "onChange",
    resolver: zodResolver(generateInvoiceSchema),
    defaultValues: {
      insurer_id: insurerOptions[0]?.value || "",
      period_type: dateFilterOptions[0]?.value || "current_month",
      period: "",
    },
  });

  const { control, setValue } = methods;

  const insurerId = useWatch({ control, name: "insurer_id" });
  const periodType = useWatch({ control, name: "period_type" });
  const period = useWatch({ control, name: "period" });

  const granularity = getPeriodGranularity(periodType);

  const periodOptions = useMemo(() => getInvoicePeriodOptions(granularity), [granularity]);

  const lastPeriodTypeRef = useRef<string | null>(null);

  useEffect(() => {
    const hasPeriodTypeChanged = lastPeriodTypeRef.current !== periodType;
    lastPeriodTypeRef.current = periodType;

    const isCurrentPeriodValid = periodOptions.some((option) => option.value === period);
    if (!hasPeriodTypeChanged && isCurrentPeriodValid) return;

    setValue("period", getDefaultPeriodForFilter(periodType, periodOptions), {
      shouldValidate: true,
    });
  }, [periodOptions, periodType, period, setValue]);

  useEffect(() => {
    if (insurerId || !insurerOptions.length) return;

    setValue("insurer_id", insurerOptions[0].value, { shouldValidate: true });
  }, [insurerOptions, insurerId, setValue]);

  const taxSplit = useMemo(() => getInvoiceTaxSplit(getInsurerCountryById(insurerId)), [insurerId]);

  const lineCount = useMemo(
    () => getPendingInvoiceLineCount(insurerId, period, granularity),
    [insurerId, period, granularity]
  );

  const [step, setStep] = useState<"form" | "preview">("form");

  const handlePreview = methods.handleSubmit(() => {
    setStep("preview");
  });

  const handleSaveDraft = methods.handleSubmit(async (values) => {
    await onSubmit(values, lineCount);
  });

  if (step === "preview") {
    return (
      <InvoicePreviewStep
        lines={[]}
        taxSplit={taxSplit}
        isSubmitting={isSubmitting}
        onBack={() => setStep("form")}
        onSaveDraft={handleSaveDraft}
      />
    );
  }

  return (
    <FormProvider {...methods}>
      <form noValidate className="space-y-4" onSubmit={(event) => event.preventDefault()}>
        {!B2C_FEATURE_FLAGS.isOCEnvironment ? (
          <SelectInput
            control={control}
            name="insurer_id"
            label="Insurer"
            placeholder="Select insurer"
            options={insurerOptions}
            searchable
            required
          />
        ) : null}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <SelectInput
            control={control}
            name="period_type"
            label="Period type"
            placeholder="Select period type"
            options={dateFilterOptions}
            required
          />
          <SelectInput
            control={control}
            name="period"
            label="Period"
            placeholder="Select period"
            options={periodOptions}
            required
          />
        </div>

        <div className="rounded-lg border border-theme-border bg-theme-surface-soft px-4 py-3 text-sm text-theme-text-soft">
          <p>
            Broker country:{" "}
            <span className="font-semibold text-theme-text">{taxSplit.brokerCountry}</span> ·
            Insurer country:{" "}
            <span className="font-semibold text-theme-text">{taxSplit.insurerCountry}</span>
          </p>
          <p className="mt-1">
            {taxSplit.taxLabel} split:{" "}
            <span className="font-semibold text-theme-text">{taxSplit.label}</span> @{" "}
            {taxSplit.ratePercent}%
          </p>
        </div>

        <div className="flex justify-end gap-3 border-t border-theme-border pt-4">
          <Button type="button" variant="secondary" disabled={isSubmitting} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={isSubmitting}
            isLoading={isSubmitting}
            onClick={handlePreview}
          >
            <span className="inline-flex items-center gap-1.5">
              {lineCount > 0
                ? `Preview ${lineCount} ${lineCount === 1 ? "line" : "lines"}`
                : "Preview"}
              <ArrowRight className="h-4 w-4" />
            </span>
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
